/**
 * 通用枚举配置类型（所有枚举都遵循这个结构）
 */
export type EnumConfig<T = number | string> = Record<
  string,
  {
    value: T // 枚举值（数字/字符串）
    label: string // 显示标签
  }
>

/**
 * 通用枚举工具类
 */
export class EnumUtil<T = number | string> {
  private config: EnumConfig<T>
  private valueMap: Map<T, string> // 缓存：值 → 标签
  private labelMap: Map<string, T> // 缓存：标签 → 值
  private allValues: T[] // 所有枚举值

  constructor(config: EnumConfig<T>) {
    this.config = config
    // 初始化缓存
    this.valueMap = new Map()
    this.labelMap = new Map()
    this.allValues = []

    // 解析配置，填充缓存
    Object.values(config).forEach((item) => {
      this.valueMap.set(item.value, item.label)
      this.labelMap.set(item.label, item.value)
      this.allValues.push(item.value)
    })
  }

  /**
   * 通过值获取标签
   * @param value 枚举值
   * @param defaultLabel 默认标签（值不存在时返回）
   */
  getLabel(value: T, defaultLabel = '未知'): string {
    return this.valueMap.get(value) || defaultLabel
  }

  /**
   * 通过标签获取值
   * @param label 显示标签
   * @param defaultValue 默认值（标签不存在时返回）
   */
  getValue(label: string, defaultValue?: T): T | undefined {
    return this.labelMap.get(label) || defaultValue
  }

  /**
   * 校验值是否为合法枚举值
   */
  isValid(value: T): boolean {
    return this.valueMap.has(value)
  }

  /**
   * 生成下拉选择器选项（适配 Element Plus/Naive UI 等）
   */
  getOptions() {
    return Object.values(this.config).map((item) => ({
      label: item.label,
      value: item.value
    }))
  }

  /**
   * 获取所有枚举值
   */
  getValues(): T[] {
    return [...this.allValues]
  }

  /**
   * 获取所有枚举标签
   */
  getLabels(): string[] {
    return [...this.valueMap.values()]
  }
}
